import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Check, X, HelpCircle, DollarSign, Plus, ExternalLink } from "lucide-react";

const STATUS_TABS = [
  { key: "", label: "All" }, { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" }, { key: "rejected", label: "Rejected" },
  { key: "needs_info", label: "Needs info" },
];

function StatCard({ label, value }) {
  return <div className="rounded-xl border border-slate-200 bg-white p-4"><p className="text-xs uppercase tracking-wider text-slate-400">{label}</p><p className="text-2xl font-semibold text-slate-900 mt-1">{value}</p></div>;
}

// ---------- Overview ----------
function Overview() {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then((r) => setA(r.data)); }, []);
  if (!a) return <Loader2 className="h-6 w-6 animate-spin text-blue-600" />;
  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Applications" value={a.counts.applications} />
        <StatCard label="Pending" value={a.counts.pending} />
        <StatCard label="Active ambassadors" value={a.counts.active_ambassadors} />
        <StatCard label="Revenue generated" value={`$${a.money.revenue_generated.toFixed(2)}`} />
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-4">Conversion funnel</h3>
        <div className="grid grid-cols-3 gap-4">
          <div><p className="text-xs text-slate-400">Clicks</p><p className="text-xl font-semibold">{a.funnel.clicks}</p></div>
          <div><p className="text-xs text-slate-400">Signups ({a.funnel.signup_conversion}%)</p><p className="text-xl font-semibold">{a.funnel.signups}</p></div>
          <div><p className="text-xs text-slate-400">Paid ({a.funnel.paid_conversion}%)</p><p className="text-xl font-semibold">{a.funnel.paid}</p></div>
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <StatCard label="Total payouts" value={`$${a.money.total_payouts.toFixed(2)}`} />
        <StatCard label="Outstanding owed" value={`$${a.money.outstanding_owed.toFixed(2)}`} />
        <StatCard label="ROI" value={`${a.money.roi}x`} />
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-4">Top ambassadors</h3>
        {a.top_ambassadors.length === 0 ? <p className="text-sm text-slate-500">No ambassadors yet.</p> : (
          <div className="divide-y divide-slate-100">
            {a.top_ambassadors.map((t) => (
              <div key={`${t.name}-${t.university}`} className="flex items-center justify-between py-2 text-sm">
                <span className="text-slate-700">{t.name} <span className="text-slate-400">· {t.university}</span></span>
                <span className="text-slate-500">{t.signups} signups · {t.paid} paid · ${t.earnings.toFixed(2)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Applications ----------
function Applications() {
  const [status, setStatus] = useState("");
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");

  const load = async () => {
    setLoading(true);
    const res = await api.get(`/admin/applications${status ? `?status=${status}` : ""}`);
    setApps(res.data); setLoading(false);
  };
  useEffect(() => { load(); }, [status]);

  const review = async (id, action) => {
    try {
      await api.post(`/admin/applications/${id}/review`, { action, note });
      toast.success(`Application ${action === "approve" ? "approved" : action === "reject" ? "rejected" : "updated"}`);
      setDetail(null); setNote(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-applications">
      <div className="flex gap-2 overflow-x-auto no-scrollbar">
        {STATUS_TABS.map((t) => (
          <button key={t.key} onClick={() => setStatus(t.key)} data-testid={`appfilter-${t.key || "all"}`}
            className={`shrink-0 rounded-full px-3 py-1.5 text-sm font-medium ${status === t.key ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{t.label}</button>
        ))}
      </div>
      {loading ? <Loader2 className="h-6 w-6 animate-spin text-blue-600" /> : apps.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-slate-500">No applications.</div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
          {apps.map((app) => (
            <div key={app.id} data-testid={`app-row-${app.id}`} className="flex items-center gap-3 px-4 py-3">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-900">{app.full_name} <span className="text-xs text-slate-400">· {app.university}</span></p>
                <p className="text-xs text-slate-500 truncate">{app.email} · {app.major} · reach {app.reach || "—"}</p>
              </div>
              <span className={`text-xs font-medium capitalize px-2 py-0.5 rounded-full ${app.status === "approved" ? "bg-emerald-50 text-emerald-700" : app.status === "rejected" ? "bg-red-50 text-red-600" : app.status === "needs_info" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{app.status}</span>
              <Button size="sm" variant="outline" onClick={() => setDetail(app)} data-testid={`view-app-${app.id}`} className="rounded-lg">View</Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-auto" data-testid="app-detail-modal">
          {detail && (
            <>
              <DialogHeader><DialogTitle>{detail.full_name}</DialogTitle></DialogHeader>
              <div className="space-y-2 text-sm text-slate-600">
                {[["Email", detail.email], ["University", detail.university], ["Major", detail.major], ["Year", detail.academic_year], ["GPA", detail.gpa], ["Grad", detail.graduation_year], ["Orgs", detail.organizations], ["Leadership", detail.leadership], ["Reach", detail.reach], ["Involvement", detail.involvement], ["TikTok", detail.tiktok], ["Instagram", detail.instagram], ["YouTube", detail.youtube], ["Followers", detail.follower_counts], ["Links", detail.profile_links], ["Why", detail.why], ["How", detail.how_promote], ["Promoted before", detail.promoted_before]].map(([k, v]) => v ? <p key={k}><span className="text-slate-400">{k}:</span> {v}</p> : null)}
                {detail.resume_file_id && <a href={`${API}/ambassador/file/${detail.resume_file_id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-600" data-testid="view-resume">Resume / media kit <ExternalLink className="h-3.5 w-3.5" /></a>}
              </div>
              <Textarea placeholder="Internal note (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="app-note" />
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button onClick={() => review(detail.id, "request_info")} variant="outline" data-testid="app-reqinfo" className="rounded-lg"><HelpCircle className="h-4 w-4 mr-1" /> Request info</Button>
                <Button onClick={() => review(detail.id, "reject")} data-testid="app-reject" className="rounded-lg bg-red-600 hover:bg-red-700 text-white"><X className="h-4 w-4 mr-1" /> Reject</Button>
                <Button onClick={() => review(detail.id, "approve")} data-testid="app-approve" className="rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white"><Check className="h-4 w-4 mr-1" /> Approve</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------- Ambassadors ----------
function Ambassadors() {
  const [ambs, setAmbs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [comp, setComp] = useState({ base_monthly: 0, per_signup: 0, per_paid: 0, bonus: 0 });
  const [stipend, setStipend] = useState("");
  const [q, setQ] = useState("");

  const load = async () => { setLoading(true); const r = await api.get("/admin/ambassadors"); setAmbs(r.data); setLoading(false); };
  useEffect(() => { load(); }, []);

  const openEdit = (a) => { setEdit(a); setComp({ base_monthly: 0, per_signup: 0, per_paid: 0, bonus: 0, ...(a.compensation || {}) }); setStipend(""); };

  const saveComp = async () => {
    await api.patch(`/admin/ambassadors/${edit.ambassador_id}`, { compensation: {
      base_monthly: Number(comp.base_monthly), per_signup: Number(comp.per_signup),
      per_paid: Number(comp.per_paid), bonus: Number(comp.bonus) } });
    toast.success("Compensation saved"); load();
  };
  const toggleStatus = async (a) => {
    await api.patch(`/admin/ambassadors/${a.ambassador_id}`, { status: a.status === "active" ? "inactive" : "active" });
    toast.success("Status updated"); load(); if (edit) setEdit({ ...edit, status: a.status === "active" ? "inactive" : "active" });
  };
  const addStipend = async () => {
    if (!stipend) return;
    await api.post(`/admin/ambassadors/${edit.ambassador_id}/stipend`, { amount: Number(stipend) });
    toast.success("Stipend added"); setStipend(""); load();
  };
  const payout = async (a) => {
    try {
      const r = await api.post(`/admin/ambassadors/${a.ambassador_id}/payout`, {});
      toast.success(`Payout of $${r.data.amount.toFixed(2)} recorded (${r.data.result}).`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Payout failed"); }
  };

  const filtered = ambs.filter((a) => !q || a.university.toLowerCase().includes(q.toLowerCase()) || a.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-4" data-testid="admin-ambassadors">
      <Input placeholder="Search by name or university…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="amb-search" className="max-w-sm" />
      {loading ? <Loader2 className="h-6 w-6 animate-spin text-blue-600" /> : filtered.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-slate-500">No ambassadors yet.</div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
          {filtered.map((a) => (
            <div key={a.ambassador_id} data-testid={`amb-row-${a.ambassador_id}`} className="flex items-center gap-3 px-4 py-3">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-900">{a.name} <span className="text-xs font-mono text-blue-600">{a.referral_code}</span></p>
                <p className="text-xs text-slate-500">{a.university} · {a.stats.signups} signups · {a.stats.paid} paid · ${a.stats.earnings_available.toFixed(2)} available</p>
              </div>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${a.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{a.status}</span>
              <Button size="sm" variant="outline" onClick={() => openEdit(a)} data-testid={`manage-amb-${a.ambassador_id}`} className="rounded-lg">Manage</Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="amb-manage-modal">
          {edit && (
            <>
              <DialogHeader><DialogTitle>{edit.name} · {edit.referral_code}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Compensation (configurable)</p>
                <div className="grid grid-cols-2 gap-3">
                  {[["base_monthly", "Base monthly ($)"], ["per_signup", "Per signup ($)"], ["per_paid", "Per paid user ($)"], ["bonus", "Bonus ($)"]].map(([k, label]) => (
                    <div key={k}><label className="text-xs text-slate-500">{label}</label>
                      <Input type="number" value={comp[k]} onChange={(e) => setComp({ ...comp, [k]: e.target.value })} data-testid={`comp-${k}`} className="mt-1" /></div>
                  ))}
                </div>
                <Button onClick={saveComp} data-testid="save-comp-btn" className="w-full bg-slate-900 hover:bg-slate-800 rounded-lg">Save compensation</Button>
                <div className="flex gap-2 items-end">
                  <div className="flex-1"><label className="text-xs text-slate-500">Add stipend / bonus ($)</label>
                    <Input type="number" value={stipend} onChange={(e) => setStipend(e.target.value)} data-testid="stipend-amount" className="mt-1" /></div>
                  <Button onClick={addStipend} variant="outline" data-testid="add-stipend-btn" className="rounded-lg"><DollarSign className="h-4 w-4" /></Button>
                </div>
                <div className="flex gap-2 pt-2 border-t border-slate-100">
                  <Button onClick={() => toggleStatus(edit)} variant="outline" data-testid="toggle-amb-status" className="flex-1 rounded-lg">{edit.status === "active" ? "Deactivate" : "Reactivate"}</Button>
                  <Button onClick={() => payout(edit)} data-testid="payout-btn" className="flex-1 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white">Pay out available</Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------- Campaigns ----------
function Campaigns() {
  const [camps, setCamps] = useState([]);
  const [subs, setSubs] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", university: "all", platforms: "", payment: 0, num_posts: 1, content_requirements: "", status: "active" });

  const load = async () => {
    const [c, s] = await Promise.all([api.get("/admin/campaigns"), api.get("/admin/campaign-submissions")]);
    setCamps(c.data); setSubs(s.data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    await api.post("/admin/campaigns", { ...form, payment: Number(form.payment), num_posts: Number(form.num_posts),
      platforms: form.platforms.split(",").map((s) => s.trim()).filter(Boolean) });
    toast.success("Campaign created"); setCreating(false);
    setForm({ name: "", description: "", university: "all", platforms: "", payment: 0, num_posts: 1, content_requirements: "", status: "active" });
    load();
  };
  const reviewSub = async (id, patch) => { await api.patch(`/admin/campaign-submissions/${id}`, patch); toast.success("Updated"); load(); };

  return (
    <div className="space-y-4" data-testid="admin-campaigns">
      <div className="flex justify-end"><Button onClick={() => setCreating(true)} data-testid="new-campaign-btn" className="bg-blue-600 hover:bg-blue-700 rounded-lg"><Plus className="h-4 w-4 mr-1" /> New campaign</Button></div>
      {camps.length === 0 ? <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-slate-500">No campaigns yet.</div> : (
        <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
          {camps.map((c) => (
            <div key={c.id} data-testid={`camp-row-${c.id}`} className="flex items-center gap-3 px-4 py-3">
              <div className="flex-1"><p className="font-medium text-slate-900">{c.name}</p><p className="text-xs text-slate-500">{c.university} · ${Number(c.payment).toFixed(2)} · {c.submission_count} submissions · {c.status}</p></div>
              <Button size="sm" variant="outline" onClick={() => reviewSub(c.id, {}) || api.patch(`/admin/campaigns/${c.id}`, { status: c.status === "active" ? "ended" : "active" }).then(load)} className="rounded-lg">{c.status === "active" ? "End" : "Activate"}</Button>
            </div>
          ))}
        </div>
      )}
      <div>
        <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-2 mt-4">Submissions</h3>
        {subs.length === 0 ? <p className="text-sm text-slate-500">No submissions.</p> : (
          <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
            {subs.map((s) => (
              <div key={s.id} data-testid={`sub-row-${s.id}`} className="flex flex-col sm:flex-row sm:items-center gap-2 px-4 py-3">
                <div className="flex-1"><p className="text-sm font-medium text-slate-900">{s.campaign_name} · {s.ambassador_name}</p>
                  <p className="text-xs text-slate-500">{Object.entries(s.links || {}).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`).join(" · ") || "no links"} · {s.notes}</p></div>
                <span className="text-xs capitalize text-slate-500">{s.status} / pay {s.payment_status}</span>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => reviewSub(s.id, { status: "approved" })} data-testid={`approve-sub-${s.id}`}>Approve</Button>
                  <Button size="sm" variant="outline" onClick={() => reviewSub(s.id, { payment_status: "paid" })} data-testid={`paysub-${s.id}`}>Mark paid</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent data-testid="new-campaign-modal">
          <DialogHeader><DialogTitle>New campaign</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Campaign name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="camp-name" />
            <Textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="camp-desc" />
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Target university (or 'all')" value={form.university} onChange={(e) => setForm({ ...form, university: e.target.value })} data-testid="camp-uni" />
              <Input placeholder="Platforms (comma sep)" value={form.platforms} onChange={(e) => setForm({ ...form, platforms: e.target.value })} data-testid="camp-platforms" />
              <Input type="number" placeholder="Payment ($)" value={form.payment} onChange={(e) => setForm({ ...form, payment: e.target.value })} data-testid="camp-payment" />
              <Input type="number" placeholder="Posts required" value={form.num_posts} onChange={(e) => setForm({ ...form, num_posts: e.target.value })} data-testid="camp-posts" />
            </div>
            <Textarea placeholder="Content requirements" value={form.content_requirements} onChange={(e) => setForm({ ...form, content_requirements: e.target.value })} data-testid="camp-reqs" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
            <Button onClick={create} data-testid="create-campaign-btn" className="bg-blue-600 hover:bg-blue-700">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------- Universities ----------
function Universities() {
  const [unis, setUnis] = useState([]);
  useEffect(() => { api.get("/admin/universities").then((r) => setUnis(r.data)); }, []);
  return (
    <div data-testid="admin-universities">
      {unis.length === 0 ? <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-slate-500">No universities yet.</div> : (
        <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
          {unis.map((u) => (
            <div key={u.name} className="flex items-center gap-3 px-4 py-3">
              <span className="flex-1 font-medium text-slate-900">{u.name}</span>
              <span className="text-xs text-slate-500">{u.ambassadors} ambassadors · {u.active} active · {u.signups} signups · {u.paid} paid</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Users ----------
function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const load = async () => { setLoading(true); const r = await api.get("/admin/users"); setUsers(r.data); setLoading(false); };
  useEffect(() => { load(); }, []);
  const toggle = async (u) => {
    try {
      const r = await api.patch(`/admin/users/${u.user_id}`, { paid_status: !u.paid_status });
      setUsers((list) => list.map((x) => x.user_id === u.user_id ? { ...x, paid_status: r.data.paid_status } : x));
      toast.success(`${u.email} is now ${r.data.paid_status ? "Pro" : "Free"}`);
    } catch (e) { toast.error("Could not update access"); }
  };
  const filtered = users.filter((u) => !q || (u.email || "").toLowerCase().includes(q.toLowerCase()) || (u.name || "").toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-4" data-testid="admin-users">
      <Input placeholder="Search by name or email…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="user-search" className="max-w-sm" />
      {loading ? <Loader2 className="h-6 w-6 animate-spin text-blue-600" /> : (
        <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
          {filtered.map((u) => (
            <div key={u.user_id} data-testid={`user-row-${u.user_id}`} className="flex items-center gap-3 px-4 py-3">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-900 truncate">{u.name} {u.is_admin && <span className="text-[10px] font-semibold text-blue-600">ADMIN</span>}</p>
                <p className="text-xs text-slate-500 truncate">{u.email} · {u.courses_count} course(s) · payment: {u.payment_status} · {u.auth_provider}</p>
              </div>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${u.paid_status ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{u.paid_status ? "Pro" : "Free"}</span>
              <Button size="sm" variant="outline" onClick={() => toggle(u)} data-testid={`toggle-pro-${u.user_id}`} className="rounded-lg">
                {u.paid_status ? "Revoke Pro" : "Grant Pro"}
              </Button>
            </div>
          ))}
          {filtered.length === 0 && <div className="p-8 text-center text-slate-500">No users found.</div>}
        </div>
      )}
    </div>
  );
}

// ---------- Settings ----------
function AdminSettings() {
  const [on, setOn] = useState(false);
  useEffect(() => { api.get("/admin/settings").then((r) => setOn(r.data.leaderboard_enabled)); }, []);
  const toggle = async (v) => { setOn(v); await api.post("/admin/settings", { leaderboard_enabled: v }); toast.success("Saved"); };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 flex items-center justify-between" data-testid="admin-settings">
      <div><p className="font-medium text-slate-900">Public campus leaderboard</p><p className="text-sm text-slate-500">Show the /leaderboard page to visitors.</p></div>
      <Switch checked={on} onCheckedChange={toggle} data-testid="leaderboard-toggle" />
    </div>
  );
}

export default function AdminPanel() {
  return (
    <div className="space-y-6">
      <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900">Campus Ambassadors</h1>
      <Tabs defaultValue="overview">
        <TabsList className="flex-wrap h-auto" data-testid="admin-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
          <TabsTrigger value="applications" data-testid="tab-applications">Applications</TabsTrigger>
          <TabsTrigger value="ambassadors" data-testid="tab-ambassadors">Ambassadors</TabsTrigger>
          <TabsTrigger value="campaigns" data-testid="tab-campaigns">Campaigns</TabsTrigger>
          <TabsTrigger value="universities" data-testid="tab-universities">Universities</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="mt-6"><Overview /></TabsContent>
        <TabsContent value="users" className="mt-6"><Users /></TabsContent>
        <TabsContent value="applications" className="mt-6"><Applications /></TabsContent>
        <TabsContent value="ambassadors" className="mt-6"><Ambassadors /></TabsContent>
        <TabsContent value="campaigns" className="mt-6"><Campaigns /></TabsContent>
        <TabsContent value="universities" className="mt-6"><Universities /></TabsContent>
        <TabsContent value="settings" className="mt-6"><AdminSettings /></TabsContent>
      </Tabs>
    </div>
  );
}
